/**
 * Mesure d'audience exemptée de consentement (08/10/2026).
 *
 * POURQUOI. Depuis l'audit RGPD du 25/09/2026 (commit 0dab7f08),
 * `VisitorTracker` ne tourne qu'après consentement explicite : la collection
 * `visitor-sessions` est passée d'environ 70 sessions par jour à environ 10.
 * Le site n'avait plus aucune mesure complète de son audience, et les
 * chiffres d'avant et d'après n'étaient plus comparables.
 *
 * CE QUE FAIT CETTE MESURE, ET RIEN DE PLUS. Un comptage côté serveur de
 * pages vues, agrégé par jour × page × source × type d'appareil, dans la
 * collection `audience-jour`. Elle s'inscrit dans l'exemption de
 * consentement des traceurs de mesure d'audience (article 82 de la loi
 * Informatique et Libertés ; CNIL, délibération n° 2020-091 du 17/09/2020,
 * lignes directrices, art. 5, § 50 à 52 ; et les conditions détaillées par la
 * CNIL dans « Cookies : solutions pour les outils de mesure d'audience » —
 * information, opposition, 13 mois pour un traceur, 25 mois pour les
 * données) :
 *
 *   - finalité unique : la mesure de l'audience de ce site, pour l'éditeur
 *     seul. Aucune donnée n'est recoupée avec `visitor-sessions`, les
 *     contacts ou les entreprises identifiées ; aucune n'est transmise à un
 *     tiers ;
 *   - aucun cookie, aucun stockage local, aucun identifiant posé sur le
 *     terminal. Le seul cookie est le REFUS (`COOKIE_REFUS`), posé à la
 *     demande expresse du visiteur ;
 *   - l'adresse IP n'est ni conservée ni transmise : elle entre, avec
 *     l'user-agent, dans un condensé HMAC salé par un sel qui change chaque
 *     jour (`empreinteVisite`). Ce condensé n'existe que dans la collection
 *     temporaire `audience-passages`, vidée chaque jour (purge quotidienne +
 *     index d'expiration Mongo) : il sert à reconnaître la page suivante
 *     d'une même visite, puis il est oublié ;
 *   - les lignes conservées (`ligneAgregat`) ne contiennent que des
 *     compteurs et des catégories : ni IP, ni condensé, ni user-agent, ni
 *     adresse complète de provenance (réduite au domaine) ;
 *   - conservation des agrégats : 25 mois au plus (`RETENTION_AUDIENCE_AGREGEE_JOURS`),
 *     purge branchée sur la purge RGPD quotidienne ;
 *   - opposition : un lien « Ne pas être compté » dans les politiques de
 *     confidentialité et de cookies pose `COOKIE_REFUS` (13 mois au plus) ;
 *     le navigateur n'envoie alors plus rien, et le serveur ignore de toute
 *     façon toute balise qui le porte.
 *
 * Tout ce fichier est fait de fonctions pures, testées dans
 * scripts/tests/audience-exemptee.test.ts.
 */
import crypto from 'crypto'

// ─────────────────────────── Constantes ───────────────────────────

/** Cookie de refus (strictement nécessaire : il mémorise l'opposition). */
export const COOKIE_REFUS = 'sco2_audience_refus'

/** 13 mois au plus pour un traceur (CNIL) : 395 jours, sans prorogation automatique. */
export const DUREE_COOKIE_REFUS_SECONDES = 395 * 24 * 60 * 60

/** 25 mois au plus pour les données (CNIL), calcul large : 25 × 31 jours, jamais plus court qu'annoncé. */
export const RETENTION_AUDIENCE_AGREGEE_JOURS = 25 * 31

/** Une visite s'arrête après 30 minutes sans page vue (convention usuelle des outils de mesure). */
export const INACTIVITE_VISITE_MS = 30 * 60 * 1000

/**
 * Seuil d'engagement côté navigateur : la balise ne part qu'après ce temps
 * de page VISIBLE, ou dès la première interaction réelle (défilement, clic,
 * toucher, touche). Voir `AudienceBalise.tsx` et le commentaire de
 * `estRobot` pour la mesure qui l'a fixé.
 */
export const SEUIL_VISIBLE_MS = 5000

export type Appareil = 'mobile' | 'tablette' | 'ordinateur'
export type Canal = 'direct' | 'organique' | 'ia' | 'social' | 'campagne' | 'referent'

// ─────────────────────────── Jour ───────────────────────────

/** Jour civil à Paris (AAAA-MM-JJ) : c'est le jour que lit l'équipe. */
export function jourParis(d: Date): string {
  // en-CA formate en AAAA-MM-JJ.
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
}

/** Lundi (AAAA-MM-JJ) de la semaine ISO d'un jour AAAA-MM-JJ. */
export function lundiDe(jour: string): string {
  const [a, m, j] = jour.split('-').map(Number)
  const d = new Date(Date.UTC(a!, (m ?? 1) - 1, j ?? 1))
  const decalage = (d.getUTCDay() + 6) % 7 // lundi = 0
  d.setUTCDate(d.getUTCDate() - decalage)
  return d.toISOString().slice(0, 10)
}

// ─────────────────────────── Page ───────────────────────────

/** Espaces privés et techniques : jamais comptés (la balise n'y est d'ailleurs pas montée). */
const PREFIXES_EXCLUS = ['/api', '/admin', '/_next', '/espace', '/administrateur', '/arpent', '/verification']

/**
 * Chemin de page normalisé, sans chaîne de requête ni fragment (qui peuvent
 * porter un identifiant ou une adresse e-mail), ou `null` si la page ne doit
 * pas être comptée.
 */
export function normaliserPage(brut: unknown): string | null {
  if (typeof brut !== 'string') return null
  let chemin = brut.trim()
  if (!chemin.startsWith('/') || chemin.startsWith('//')) return null
  chemin = chemin.split(/[?#]/)[0] ?? ''
  try { chemin = decodeURIComponent(chemin) } catch { /* chemin mal encodé : gardé tel quel */ }
  chemin = chemin.toLowerCase().replace(/\/{2,}/g, '/')
  if (chemin.length > 1) chemin = chemin.replace(/\/+$/, '')
  if (PREFIXES_EXCLUS.some((p) => chemin === p || chemin.startsWith(`${p}/`))) return null
  // Une adresse e-mail ou une suite de chiffres longue dans le chemin n'a rien
  // à faire dans une statistique : elle est masquée.
  chemin = chemin.replace(/[^/\s]+@[^/\s]+/g, '…').replace(/\d{6,}/g, '…')
  if (chemin.length > 160) chemin = `${chemin.slice(0, 159)}…`
  return chemin || '/'
}

// ─────────────────────────── Provenance ───────────────────────────

/** Domaine du référent, sans « www. », ou `null` (aucun, illisible, ou interne). */
export function domaineReferent(brut: unknown, hoteSite?: string): string | null {
  if (typeof brut !== 'string' || !brut.trim()) return null
  let hote = brut.trim().toLowerCase()
  try {
    hote = hote.includes('://') ? new URL(hote).hostname : (hote.split('/')[0] ?? '')
  } catch {
    return null
  }
  hote = hote.replace(/^www\./, '').replace(/:\d+$/, '')
  if (!/^[a-z0-9.-]{1,253}$/.test(hote) || !hote.includes('.')) return null
  if (hoteSite && hote === hoteSite.toLowerCase().replace(/^www\./, '').replace(/:\d+$/, '')) return null
  return hote
}

/** `utm_source` assaini : minuscules, caractères simples, 40 au plus. */
export function nettoyerUtm(brut: unknown): string | null {
  if (typeof brut !== 'string') return null
  const v = brut.trim().toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, 40)
  return v || null
}

const MOTEURS = /(^|\.)(google\.[a-z.]+|bing\.com|yahoo\.[a-z.]+|duckduckgo\.com|ecosia\.org|qwant\.com|lilo\.org|startpage\.com|search\.brave\.com|yandex\.[a-z.]+|baidu\.com)$/
const ASSISTANTS_IA = /(^|\.)(chatgpt\.com|chat\.openai\.com|perplexity\.ai|claude\.ai|gemini\.google\.com|copilot\.microsoft\.com|chat\.mistral\.ai|you\.com)$/
const RESEAUX = /(^|\.)(linkedin\.com|lnkd\.in|facebook\.com|instagram\.com|twitter\.com|x\.com|t\.co|youtube\.com|bsky\.app|threads\.net)$/

/**
 * Source et canal d'une visite. La source est ce qui est stocké (domaine du
 * référent, `utm:<valeur>` ou `direct`) ; le canal la range dans une
 * catégorie lisible, dont « organique » (moteurs de recherche).
 */
export function classerSource(referent: string | null, utm: string | null): { source: string; canal: Canal } {
  if (utm) return { source: `utm:${utm}`, canal: 'campagne' }
  if (!referent) return { source: 'direct', canal: 'direct' }
  if (ASSISTANTS_IA.test(referent)) return { source: referent, canal: 'ia' }
  if (MOTEURS.test(referent)) return { source: referent, canal: 'organique' }
  if (RESEAUX.test(referent)) return { source: referent, canal: 'social' }
  return { source: referent, canal: 'referent' }
}

// ─────────────────────────── Appareil ───────────────────────────

export function typeAppareil(ua: string): Appareil {
  if (/ipad|tablet|kindle|silk|(android(?!.*mobile))/i.test(ua)) return 'tablette'
  if (/mobile|iphone|ipod|android|windows phone/i.test(ua)) return 'mobile'
  return 'ordinateur'
}

// ─────────────────────────── Robots ───────────────────────────

const UA_ROBOT = /\bbot\b|bot[/;)]|crawl|spider|slurp|scrap|fetch|preview|monitor|uptime|checker|headless|phantom|puppeteer|playwright|selenium|webdriver|lighthouse|pagespeed|gtmetrix|curl|wget|python|httpclient|okhttp|java\/|go-http|axios|node-fetch|undici|libwww|facebookexternalhit|whatsapp|telegram|discord|slack|semrush|ahrefs|mj12|petalbot|bytespider|gptbot|chatgpt-user|oai-searchbot|claudebot|claude-web|anthropic|perplexity|ccbot|dataforseo|sprious|oxylabs|yandex|baidu|applebot|bingpreview|google-inspectiontool|googleother/i

/**
 * Robot reconnu par son user-agent (ou absence d'user-agent).
 *
 * L'user-agent ne suffit pas : de mai à septembre 2026, un robot a produit
 * 3 192 sessions sur /blog (40 % du trafic) avec cinq user-agents Chrome
 * ordinaires en rotation, une page, 0 seconde (constat du 14/09/2026). Il
 * exécutait le JavaScript (l'ancien traceur est en JavaScript), donc la
 * simple exécution d'un script ne le trahit pas. Mesuré en production le
 * 08/10/2026 sur 140 jours : sur les 4 091 sessions marquées automatisées,
 * AUCUNE n'a défilé, aucune n'a vu une deuxième page, toutes ont une durée
 * nulle. Le critère fiable est donc l'ENGAGEMENT : la balise ne part du
 * navigateur qu'après `SEUIL_VISIBLE_MS` de page visible ou une interaction
 * réelle, et jamais si `navigator.webdriver` est vrai (navigateur piloté).
 * Ce filtre-ci, côté serveur, est le second rideau.
 */
export function estRobot(ua: string | null | undefined): boolean {
  if (!ua || ua.length < 20) return true
  return UA_ROBOT.test(ua)
}

// ─────────────────────────── Opposition ───────────────────────────

/** Retour après « Ne pas être compté » : un chemin du site, jamais une redirection ouverte. */
export function cheminDeRetour(brut: string | null): string {
  if (!brut || !brut.startsWith('/') || brut.startsWith('//') || brut.includes('\\')) return '/cookies'
  return (brut.split('#')[0] ?? '').split('?')[0]?.slice(0, 200) || '/cookies'
}

/** Vrai si l'en-tête Cookie porte le refus d'être compté. */
export function refusAudience(enteteCookie: string | null | undefined): boolean {
  if (!enteteCookie) return false
  return enteteCookie.split(';').some((c) => c.trim() === `${COOKIE_REFUS}=1`)
}

// ─────────────────────────── Visite ───────────────────────────

/**
 * Condensé éphémère d'une visite : HMAC-SHA256 de (IP, user-agent) avec un
 * sel propre au jour. L'IP n'est jamais conservée ; le condensé change chaque
 * jour, n'est pas réversible sans le secret, et n'est gardé que dans
 * `audience-passages`, vidée chaque jour. Il ne sert qu'à relier les pages
 * d'une même visite. Préfixe distinct de `hashClientIpLe` (rate-limit.ts) :
 * les deux condensés ne sont jamais égaux, donc jamais recoupables.
 */
export function empreinteVisite(ip: string, ua: string, jour: string, secret: string): string {
  return crypto
    .createHmac('sha256', `audience:${secret}:${jour}`)
    .update(`${ip}\n${ua}`)
    .digest('hex')
    .slice(0, 32)
}

/** Une page vue ouvre-t-elle une nouvelle visite ? */
export function estNouvelleVisite(derniereVue: Date | string | null | undefined, maintenant: Date): boolean {
  if (!derniereVue) return true
  const t = new Date(derniereVue).getTime()
  if (!Number.isFinite(t)) return true
  return maintenant.getTime() - t > INACTIVITE_VISITE_MS
}

// ─────────────────────────── Ligne agrégée ───────────────────────────

/** Les SEULS champs écrits dans `audience-jour`. Vérifié par les tests. */
export const CHAMPS_AGREGAT = ['jour', 'page', 'source', 'canal', 'appareil', 'pagesVues', 'visites', 'visiteurs'] as const

export interface CleAgregat {
  jour: string
  page: string
  source: string
  canal: Canal
  appareil: Appareil
}

export interface Increments {
  pagesVues: number
  visites: number
  visiteurs: number
}

/**
 * Ce qu'une page vue ajoute à l'agrégat : la clé de la ligne et les
 * incréments. `visites` n'est compté que sur la page d'ENTRÉE d'une visite
 * (donc somme des visites = nombre de visites, et visites par page = entrées
 * par page) ; `visiteurs` seulement à la première page du jour d'un même
 * condensé.
 */
export function ligneAgregat(p: {
  jour: string
  page: string
  source: string
  canal: Canal
  appareil: Appareil
  nouvelleVisite: boolean
  nouveauVisiteur: boolean
}): { cle: CleAgregat; inc: Increments } {
  return {
    cle: { jour: p.jour, page: p.page, source: p.source, canal: p.canal, appareil: p.appareil },
    inc: { pagesVues: 1, visites: p.nouvelleVisite ? 1 : 0, visiteurs: p.nouveauVisiteur ? 1 : 0 },
  }
}

// ─────────────────────────── Lecture : séries hebdomadaires ───────────────────────────

export interface LigneAudience {
  jour: string
  page: string
  source: string
  canal: string
  appareil: string
  pagesVues?: number
  visites?: number
  visiteurs?: number
}

export interface SemaineAudience {
  /** Lundi de la semaine (AAAA-MM-JJ). */
  semaine: string
  visites: number
  pagesVues: number
  visitesOrganiques: number
  /** Part organique des visites, en % (entier). */
  partOrganique: number
  /** Sessions consenties (`visitor-sessions`, robots exclus), pour comparaison. */
  sessionsConsenties: number
}

export interface SyntheseAudience {
  semaines: SemaineAudience[]
  totaux: { visites: number; pagesVues: number; visitesOrganiques: number; partOrganique: number; sessionsConsenties: number }
  pagesEntree: { page: string; visites: number }[]
  canaux: { canal: string; visites: number }[]
  appareils: { appareil: string; visites: number }[]
}

const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0)

/**
 * Synthèse par semaine ISO des lignes `audience-jour`, avec les sessions
 * consenties (dates de création) à côté pour comparaison. Les semaines sans
 * aucune donnée entre la première et la dernière sont présentes à zéro : une
 * courbe ne doit pas sauter un creux.
 */
export function syntheseHebdo(
  lignes: LigneAudience[],
  sessionsConsenties: { createdAt: string | Date }[] = [],
  bornes?: { du: string; au: string },
  maxPages = 10,
): SyntheseAudience {
  const parSemaine = new Map<string, SemaineAudience>()
  const vide = (semaine: string): SemaineAudience => ({ semaine, visites: 0, pagesVues: 0, visitesOrganiques: 0, partOrganique: 0, sessionsConsenties: 0 })
  const semaineDe = (s: string) => {
    const k = lundiDe(s)
    let e = parSemaine.get(k)
    if (!e) { e = vide(k); parSemaine.set(k, e) }
    return e
  }

  const entrees = new Map<string, number>()
  const canaux = new Map<string, number>()
  const appareils = new Map<string, number>()
  for (const l of lignes) {
    const e = semaineDe(l.jour)
    const v = l.visites || 0
    e.visites += v
    e.pagesVues += l.pagesVues || 0
    if (l.canal === 'organique') e.visitesOrganiques += v
    if (v > 0) {
      entrees.set(l.page, (entrees.get(l.page) || 0) + v)
      canaux.set(l.canal, (canaux.get(l.canal) || 0) + v)
      appareils.set(l.appareil, (appareils.get(l.appareil) || 0) + v)
    }
  }
  for (const s of sessionsConsenties) {
    const d = typeof s.createdAt === 'string' ? new Date(s.createdAt) : s.createdAt
    if (Number.isNaN(d.getTime())) continue
    semaineDe(jourParis(d)).sessionsConsenties += 1
  }

  // Semaines continues entre les bornes (ou la première et la dernière vue).
  const cles = [...parSemaine.keys()].sort()
  const debut = bornes ? lundiDe(bornes.du) : cles[0]
  const fin = bornes ? lundiDe(bornes.au) : cles[cles.length - 1]
  const semaines: SemaineAudience[] = []
  if (debut && fin) {
    const d = new Date(`${debut}T00:00:00Z`)
    const f = new Date(`${fin}T00:00:00Z`)
    while (d <= f) {
      const k = d.toISOString().slice(0, 10)
      const e = parSemaine.get(k) ?? vide(k)
      e.partOrganique = pct(e.visitesOrganiques, e.visites)
      semaines.push(e)
      d.setUTCDate(d.getUTCDate() + 7)
    }
  }

  const tot = semaines.reduce(
    (t, s) => ({
      visites: t.visites + s.visites,
      pagesVues: t.pagesVues + s.pagesVues,
      visitesOrganiques: t.visitesOrganiques + s.visitesOrganiques,
      sessionsConsenties: t.sessionsConsenties + s.sessionsConsenties,
    }),
    { visites: 0, pagesVues: 0, visitesOrganiques: 0, sessionsConsenties: 0 },
  )
  const trier = (m: Map<string, number>) => [...m.entries()].sort((a, b) => b[1] - a[1])

  return {
    semaines,
    totaux: { ...tot, partOrganique: pct(tot.visitesOrganiques, tot.visites) },
    pagesEntree: trier(entrees).slice(0, maxPages).map(([page, visites]) => ({ page, visites })),
    canaux: trier(canaux).map(([canal, visites]) => ({ canal, visites })),
    appareils: trier(appareils).map(([appareil, visites]) => ({ appareil, visites })),
  }
}

/** Totaux d'une fenêtre de jours [du, au] inclus, pour les rapports. */
export function totauxFenetre(lignes: LigneAudience[], du: string, au: string) {
  let visites = 0, pagesVues = 0, organiques = 0
  const pages = new Map<string, number>()
  const vues = new Map<string, number>()
  const sources = new Map<string, number>()
  for (const l of lignes) {
    if (l.jour < du || l.jour > au) continue
    const v = l.visites || 0
    visites += v
    pagesVues += l.pagesVues || 0
    if (l.canal === 'organique') organiques += v
    if (v) pages.set(l.page, (pages.get(l.page) || 0) + v)
    if (v) sources.set(l.canal, (sources.get(l.canal) || 0) + v)
    vues.set(l.page, (vues.get(l.page) || 0) + (l.pagesVues || 0))
  }
  const trier = (m: Map<string, number>) => [...m.entries()].sort((a, b) => b[1] - a[1])
  return {
    visites,
    pagesVues,
    partOrganique: pct(organiques, visites),
    pagesEntree: trier(pages).map(([page, n]) => ({ page, visites: n })),
    pagesVuesParPage: trier(vues).map(([page, n]) => ({ page, vues: n })),
    canaux: trier(sources).map(([canal, n]) => ({ canal, visites: n })),
  }
}
